import { ReactElement, useCallback, useMemo } from 'react';

import { Box, Flex, Group } from '@mantine/core';
import { IconTriangleInvertedFilled } from '@tabler/icons-react';
import Select, {
  ClassNamesConfig,
  components,
  DropdownIndicatorProps,
  GroupBase,
  MultiValue,
  SingleValue,
  StylesConfig,
  ValueContainerProps
} from 'react-select';

import { plum } from '@/styles/colors';

import { LabelAndValue } from '@/types/custom';

import LabelText, { LabelTextProps } from '../LabelText';

import { selectStyles } from './selectStyles';

export interface DropdownProps<T extends LabelAndValue> {
  isMulti?: false;
  options: T[];
  placeholder?: string;
  onChange: (value: T | null) => void;
  label?: React.ReactNode;
  isDisabled?: boolean;
  classNames?: ClassNamesConfig<T, true, GroupBase<T>>;
  maxItemsToDisplay?: number;
  value?: T;
  labelTextProps?: Partial<LabelTextProps>;
  showClear?: boolean;
  required?: boolean;
  labelVariant?: 'allCaps' | 'regular' | undefined;
  customComponents?: {
    Option?: React.ComponentType<any>;
  };
}

export interface MultiDropdownProps<T extends LabelAndValue>
  extends Omit<DropdownProps<T>, 'onChange' | 'value' | 'isMulti'> {
  isMulti: true;
  onChange: (value: T[] | null) => void;
  value?: T[];
}

export type DropdownComponentProps<T extends LabelAndValue> =
  | DropdownProps<T>
  | MultiDropdownProps<T>;

export default function Dropdown<T extends LabelAndValue>(props: DropdownComponentProps<T>) {
  const {
    isMulti = false,
    options,
    placeholder,
    onChange,
    label,
    isDisabled,
    classNames,
    value,
    labelTextProps,
    showClear = false,
    required = false,
    labelVariant = 'allCaps',
    customComponents
  } = props;

  const handleOnChange = useCallback(
    (selectedValue: SingleValue<T> | MultiValue<T>) => {
      if (isMulti) {
        (onChange as (value: T[] | null) => void)(
          Array.isArray(selectedValue) ? selectedValue : []
        );
      } else {
        (onChange as (value: T | null) => void)((selectedValue as T) || null);
      }
    },
    [isMulti, onChange]
  );

  const selectValue = useMemo(() => {
    if (isMulti) {
      return Array.isArray(value) ? value : [];
    }
    if (Array.isArray(value)) {
      return null;
    }
    // When value is null or undefined, return null to clear the selection
    if (value == undefined) {
      return null;
    }
    return value;
  }, [isMulti, value]);

  const CustomSingleValue = (props: any) => {
    const { inputValue, menuIsOpen } = props.selectProps;
    const isTyping = !inputValue && menuIsOpen;
    if (isTyping) return null;
    return <components.SingleValue {...props} />;
  };
  const handleClear = () => {
    if (isMulti) {
      (onChange as (value: T[] | null) => void)([]);
    } else {
      // Send empty string wrapped in object with empty label/value if T has that shape
      (onChange as (value: T | null) => void)({ label: '', value: '' } as T);
    }
  };

  return (
    <Flex direction="column">
      {typeof label === 'string' ? (
        <LabelText
          variant={labelVariant}
          label={label}
          required={required}
          size="xs"
          {...labelTextProps}
        />
      ) : (
        label
      )}
      <Select<T, true>
        isMulti={isMulti || undefined}
        isClearable={showClear}
        value={selectValue}
        options={options}
        placeholder={placeholder}
        onChange={(val, actionMeta) => {
          if (actionMeta.action === 'clear') {
            handleClear();
          } else {
            handleOnChange(val);
          }
        }}
        isDisabled={isDisabled}
        styles={selectStyles as unknown as StylesConfig<T, true>}
        classNames={classNames}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        menuPortalTarget={document.body}
        menuPlacement="auto"
        components={{
          ValueContainer: CustomValueContainer,
          DropdownIndicator: CustomDropdownIndicator,
          SingleValue: CustomSingleValue,
          ...(customComponents?.Option && { Option: customComponents.Option })
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: plum[6],
            primary75: plum[5],
            primary50: plum[4],
            primary25: plum[2]
          }
        })}
      />
    </Flex>
  );
}

const CustomValueContainer = <T extends LabelAndValue>({
  children,
  ...props
}: ValueContainerProps<T, true>) => {
  const selectProps = props.selectProps as unknown as DropdownProps<T>;
  const { isMulti } = selectProps;

  if (!isMulti) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  const [values, input] = children as ReactElement[];
  const selectedCount = props.getValue().length;

  // Show individual pills
  if (selectedCount > 0) {
    return (
      <components.ValueContainer {...props}>
        <Group gap={4}>{values}</Group>
        {input}
      </components.ValueContainer>
    );
  }

  // Default case
  return (
    <components.ValueContainer {...props}>
      {values}
      {input}
    </components.ValueContainer>
  );
};

const CustomDropdownIndicator = <T extends LabelAndValue>({
  ...props
}: DropdownIndicatorProps<T, true>) => {
  return (
    <components.DropdownIndicator {...props}>
      <Box>
        <IconTriangleInvertedFilled size={10} color={plum[6]} />
      </Box>
    </components.DropdownIndicator>
  );
};
